import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Autocomplete,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import DonutLargeRoundedIcon from "@mui/icons-material/DonutLargeRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import SidebarLayout from "../components/sidebarLayout";
import { localizedErrorMessage } from "../utils/italianErrorMessage";
import { getAllOrdini } from "../APIs/OrdersAPI";
import { getReservationEvents } from "../APIs/ReservationEventsAPI";
import { getStatsProducts, getStatsSales } from "../APIs/StatsAPI";
import { useTranslation } from "react-i18next";
import { Doughnut, Line } from "react-chartjs-2";
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import {
  aggregateSoldRowsByArticle,
  isTastingMenuOrderPackage,
} from "../utils/statisticsSoldRows";

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const CHART_COLORS = ["#F59E0B", "#0F766E", "#2563EB", "#DC2626", "#7C3AED", "#0891B2"];

function chartColors(count) {
  return Array.from({ length: count }, (_, index) => (
    CHART_COLORS[index]
    || `hsl(${Math.round((index * 137.508) % 360)} 72% ${index % 2 ? 43 : 52}%)`
  ));
}

const ACTIVE_EVENT_STATUSES = new Set(["PREVENTIVO", "TO_CONFIRM", "CONFIRMED"]);
const CANCELLED_EVENT_STATUSES = new Set(["CANCELLED", "NO_SHOW"]);
const KNOWN_PAYMENT_METHODS = ["CASH", "CARD", "VOUCHER", "ROOM_CHARGE", "INVOICE"];

function toDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toInputDate(date) {
  const d = toDate(date) || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDate(value, withTime = false) {
  const date = toDate(value);
  if (!date) return "-";
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
  });
}

function formatTime(value) {
  const date = toDate(value);
  if (!date) return "-";
  return date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function toInputDateTime(date, fallbackDate) {
  const d = toDate(date) || toDate(fallbackDate) || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function weightUnitForProduct(product) {
  if (!product?.pricePerWeight && !product?.typeOfWeight) return "";
  if (product?.typeOfWeight === "HECTOGRAM") return "hg";
  if (product?.typeOfWeight === "KILOGRAM") return "kg";
  return "";
}

function normalizeWeightUnit(unit) {
  if (!unit) return "";
  const value = String(unit).trim();
  const upper = value.toUpperCase();
  if (upper === "HECTOGRAM" || upper === "HG") return "hg";
  if (upper === "KILOGRAM" || upper === "KG") return "kg";
  return value;
}

function formatQuantity(value, unit = "") {
  const quantity = Number(value || 0).toLocaleString("it-IT");
  return unit ? `${quantity} ${unit}` : quantity;
}

function sameDay(a, b) {
  const da = toDate(a);
  const db = toDate(b);
  if (!da || !db) return false;
  return startOfDay(da).getTime() === startOfDay(db).getTime();
}

function inRange(value, from, to) {
  const date = toDate(value);
  if (!date) return false;
  return date >= startOfDay(from) && date <= endOfDay(to);
}

function statusColor(status) {
  if (["PAID", "COMPLETED", "CONFIRMED"].includes(status)) return "success";
  if (["OPEN", "IN_PROGRESS", "TO_CONFIRM", "PREVENTIVO"].includes(status)) return "warning";
  if (["CANCELLED", "NO_SHOW"].includes(status)) return "error";
  return "default";
}

function recipientLabel(event, t) {
  if (event?.company?.name) return event.company.name;
  if (event?.client)
    return (
      [event.client.name, event.client.surname].filter(Boolean).join(" ") ||
      event.client.email ||
      t("client")
    );
  return t("reports.recipientMissing");
}

function tableLabel(day, t) {
  const tables = Array.isArray(day?.tableLinks) ? day.tableLinks : [];
  return (
    tables
      .map((link) => link?.table?.name)
      .filter(Boolean)
      .join(", ") || t("reports.tablesMissing")
  );
}

function usageTypeFromTableLink(link) {
  if (link?.usageType?.name) return link.usageType;
  const usageTypeId = Number(link?.usageTypeId);
  return Number.isInteger(usageTypeId) && usageTypeId > 0
    ? { id: usageTypeId, name: `#${usageTypeId}` }
    : null;
}

function usageTypesForDay(day) {
  const byKey = new Map();
  (Array.isArray(day?.tableLinks) ? day.tableLinks : []).forEach((link) => {
    const usageType = usageTypeFromTableLink(link);
    const key = usageType?.id || "none";
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        name: usageType?.name,
      });
    }
  });
  return byKey.size
    ? [...byKey.values()]
    : [{ key: "none", name: "" }];
}

function lineTotal(line, fallbackUnitPrice) {
  const unit = Number(line?.unitPrice ?? fallbackUnitPrice ?? 0);
  const qty = Number(line?.quantity || 0);
  return unit * qty;
}

function uniqueByValue(options) {
  const map = new Map();
  options.forEach((option) => {
    if (option?.value == null || option.value === "") return;
    if (!map.has(String(option.value))) map.set(String(option.value), option);
  });
  return [...map.values()].sort((a, b) => String(a.label).localeCompare(String(b.label), "it"));
}

function uniqueValues(values) {
  return [...new Set((Array.isArray(values) ? values : []).filter(Boolean).map(String))];
}

function categoryOptionsFromProduct(product) {
  return (Array.isArray(product?.categories) ? product.categories : [])
    .map((entry) => ({
      value: String(entry?.categoryId ?? entry?.category?.id ?? ""),
      label: entry?.category?.name || "",
    }))
    .filter((entry) => entry.value && entry.label);
}

function categoriesFromPackage(pkg) {
  return uniqueByValue(
    (Array.isArray(pkg?.items) ? pkg.items : []).flatMap((item) =>
      categoryOptionsFromProduct(item?.product)
    )
  );
}

function paymentLabel(method, rt) {
  const labels = {
    CASH: ["paymentCash", "Cash"],
    CARD: ["paymentCard", "Cards"],
    VOUCHER: ["paymentVoucher", "Suspended payments"],
    ROOM_CHARGE: ["paymentRoomCharge", "Hotel guest"],
    INVOICE: ["paymentInvoice", "Invoice"],
  };
  const entry = labels[method];
  return entry ? rt(entry[0], { defaultValue: entry[1] }) : method || "-";
}

function operatorLabel(operator, fallbackLabel) {
  if (!operator) return fallbackLabel;
  return [operator.name, operator.surname].filter(Boolean).join(" ").trim() || operator.email || `#${operator.id}`;
}

function orderSaleDate(order) {
  const paymentDates = (Array.isArray(order?.payments) ? order.payments : [])
    .map((payment) => toDate(payment?.createdAt))
    .filter(Boolean)
    .sort((a, b) => b - a);
  return toDate(order?.closedAt) || paymentDates[0] || toDate(order?.createdAt);
}

function orderSaleType(order) {
  const payments = Array.isArray(order?.payments) ? order.payments : [];
  const hasHotelCharge = payments.some((payment) =>
    String(payment?.method || "").toUpperCase() === "ROOM_CHARGE" ||
    Boolean(payment?.heliteRoomNumber)
  );
  return hasHotelCharge ? "HOTEL" : "PASSANTE";
}

const TIME_SLOT_FILTERS = [
  { value: "MORNING", labelKey: "timeSlotMorning", defaultLabel: "Morning 06:00-12:00", start: 6 * 60, end: 12 * 60 },
  { value: "LUNCH", labelKey: "timeSlotLunch", defaultLabel: "Lunch 12:00-15:00", start: 12 * 60, end: 15 * 60 },
  { value: "AFTERNOON", labelKey: "timeSlotAfternoon", defaultLabel: "Afternoon 15:00-18:00", start: 15 * 60, end: 18 * 60 },
  { value: "DINNER", labelKey: "timeSlotDinner", defaultLabel: "Dinner 18:00-24:00", start: 18 * 60, end: 24 * 60 },
  { value: "NIGHT", labelKey: "timeSlotNight", defaultLabel: "Night 00:00-06:00", start: 0, end: 6 * 60 },
];

function matchesTimeSlot(dateValue, selectedSlots) {
  if (!selectedSlots.length) return true;
  const date = toDate(dateValue);
  if (!date) return false;
  const minutes = date.getHours() * 60 + date.getMinutes();
  return TIME_SLOT_FILTERS
    .filter((slot) => selectedSlots.includes(slot.value))
    .some((slot) => minutes >= slot.start && minutes < slot.end);
}

function orderTotal(order) {
  const products = Array.isArray(order?.orderProducts) ? order.orderProducts : [];
  const packages = Array.isArray(order?.orderPackages) ? order.orderPackages : [];
  const productsTotal = products.reduce(
    (sum, line) => sum + lineTotal(line, line?.product?.price),
    0
  );
  const packagesTotal = packages.reduce(
    (sum, line) => sum + lineTotal(line, line?.package?.price),
    0
  );
  const subtotal = productsTotal + packagesTotal;
  return Math.max(
    0,
    subtotal * (1 + Number(order?.totalDiscount || 0) / 100)
      + Number(order?.surchargeAmount || 0)
      - Number(order?.discountAmount || 0)
  );
}

function orderArticleCount(order) {
  return (
    (Array.isArray(order?.orderProducts) ? order.orderProducts.length : 0) +
    (Array.isArray(order?.orderPackages) ? order.orderPackages.length : 0)
  );
}

function eventDayTotal(day) {
  const services = Array.isArray(day?.services) ? day.services : [];
  const packages = Array.isArray(day?.packages) ? day.packages : [];
  return (
    services.reduce((sum, line) => sum + lineTotal(line, line?.product?.price), 0) +
    packages.reduce((sum, line) => sum + lineTotal(line, line?.package?.price), 0)
  );
}

function flattenEventDays(events, t) {
  return (Array.isArray(events) ? events : []).flatMap((event) =>
    (Array.isArray(event.days) ? event.days : []).map((day) => ({
      ...day,
      event,
      eventName: event.eventName,
      eventStatus: event.status,
      recipient: recipientLabel(event, t),
    }))
  );
}

function isQuotePractice(event) {
  return (
    event?.practiceType === "PREVENTIVO" ||
    event?.status === "PREVENTIVO" ||
    Boolean(event?.quoteConvertedAt) ||
    Boolean(event?.quoteEmail) ||
    Number(event?.quoteSendCount || 0) > 0 ||
    Boolean(event?.quoteLastSentAt)
  );
}

// FIX 4: replaced t("reports.service") → rt("service") and t("reports.package") → rt("package")
function serviceRowsForDay(day, rt, t) {
  const event = day?.event || {};
  const base = {
    eventName: event.eventName || day.eventName,
    recipient: recipientLabel(event, t),
    status: event.status || day.eventStatus,
    startDate: day.startDate,
    endDate: day.endDate,
    tables: tableLabel(day, t),
  };

  const services = (Array.isArray(day?.services) ? day.services : []).map((line) => ({
    ...base,
    id: `svc-${day.id}-${line.id}`,
    type: rt("service"),                                                        // FIX 4
    name:
      [line?.product?.name, line?.variant?.name].filter(Boolean).join(" + ") ||
      rt("service"),                                                            // FIX 4
    quantity: Number(line?.quantity || 0),
    unitPrice: Number(line?.unitPrice ?? line?.product?.price ?? 0),
    total: lineTotal(line, line?.product?.price),
  }));

  const packages = (Array.isArray(day?.packages) ? day.packages : []).map((line) => ({
    ...base,
    id: `pkg-${day.id}-${line.id}`,
    type: rt("package"),                                                        // FIX 4
    name: line?.package?.name || rt("package"),                                // FIX 4
    quantity: Number(line?.quantity || 0),
    unitPrice: Number(line?.unitPrice ?? line?.package?.price ?? 0),
    total: lineTotal(line, line?.package?.price),
  }));

  return [...services, ...packages];
}

function StatCard({ title, value, subtitle, icon, color = "#D47200", progress }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7 }}
            >
              {title}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 900 }}>
              {value}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color,
              background: `${color}1f`,
            }}
          >
            {icon}
          </Box>
        </Stack>
        {progress !== undefined ? (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, progress))}
            sx={{ mt: 2, height: 8, borderRadius: 99 }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function SalesDonutCard({
  title,
  subtitle,
  labels,
  values,
  centerValue,
  emptyLabel,
  valueFormatter = formatMoney,
}) {
  const hasData = values.some((value) => Number(value) > 0);
  const data = useMemo(() => ({
    labels,
    datasets: [{
      data: hasData ? values : [1],
      backgroundColor: hasData ? chartColors(values.length) : ["#D1D5DB"],
      borderColor: "transparent",
      borderWidth: 0,
      hoverBorderColor: "rgba(255,255,255,0.9)",
      hoverBorderWidth: 3,
      hoverOffset: 8,
      spacing: 3,
      borderRadius: 7,
    }],
  }), [hasData, labels, values]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: "72%",
    animation: { animateRotate: true, animateScale: true, duration: 900, easing: "easeOutQuart" },
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 8, padding: 16, font: { weight: 700 } },
      },
      tooltip: {
        enabled: hasData,
        padding: 12,
        cornerRadius: 10,
        callbacks: { label: (context) => `${context.label}: ${valueFormatter(context.raw)}` },
      },
    },
  }), [hasData, valueFormatter]);

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        background: "linear-gradient(145deg, rgba(245,158,11,0.09), rgba(15,118,110,0.025) 52%, transparent)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box minWidth={0}>
            <Typography variant="h6" fontWeight={950}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          </Box>
          <Box sx={{ width: 38, height: 38, borderRadius: 2.5, display: "grid", placeItems: "center", color: "#D97706", bgcolor: "rgba(245,158,11,0.14)" }}>
            <DonutLargeRoundedIcon />
          </Box>
        </Stack>
        <Box sx={{ height: 245, position: "relative", mt: 1 }}>
          <Doughnut data={data} options={options} />
          <Box
            sx={{
              position: "absolute",
              inset: "42% 0 auto",
              transform: "translateY(-50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={800}>
              {hasData ? centerValue : emptyLabel}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function MultiFilter({ label, value, options, onChange, disabled = false }) {
  const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const selectedOptions = useMemo(
    () => options.filter((option) => selected.includes(String(option.value))),
    [options, selected]
  );

  return (
    <Autocomplete
      multiple
      size="small"
      fullWidth
      disableCloseOnSelect
      disabled={disabled}
      options={options}
      value={selectedOptions}
      getOptionLabel={(option) => option?.label || ""}
      isOptionEqualToValue={(option, selectedOption) =>
        String(option?.value) === String(selectedOption?.value)
      }
      onChange={(_, selectedItems) =>
        onChange(selectedItems.map((item) => String(item.value)))
      }
      renderOption={(props, option, { selected: isSelected }) => (
        <li {...props}>
          <Checkbox checked={isSelected} sx={{ mr: 1 }} />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedOptions.length ? "" : label}
        />
      )}
    />
  );
}

const Statistiche = () => {
  const { t } = useTranslation();
  const rt = useCallback((key, options) => t(`reports.${key}`, options), [t]);
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const isConferenceTenant = tenantType === "SALE_CONGRESSI";
  const today = useMemo(() => startOfDay(new Date()), []);

  const [periodStart, setPeriodStart] = useState(
    toInputDate(new Date(today.getFullYear(), today.getMonth(), 1))
  );
  const [periodEnd, setPeriodEnd] = useState(
    toInputDate(new Date(today.getFullYear(), today.getMonth() + 1, 0))
  );
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [salesStats, setSalesStats] = useState([]);
  const [productStats, setProductStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showEventsSection, setShowEventsSection] = useState(false);
  const [ordersViewMode, setOrdersViewMode] = useState("accounts"); // "accounts" | "orders"
  const [soldSearch, setSoldSearch] = useState("");
  const [soldRowsPage, setSoldRowsPage] = useState(0);
  const [soldRowsPerPage, setSoldRowsPerPage] = useState(25);
  const [salesFilters, setSalesFilters] = useState(() => ({
    categoryIds: [],
    articleKeys: [],
    roomIds: [],
    operatorIds: [],
    timeSlots: [],
    saleTypes: [],
    paymentMethods: [],
    startDateTime: toInputDateTime(startOfDay(new Date(periodStart))),
    endDateTime: toInputDateTime(endOfDay(new Date(periodEnd))),
  }));

  const fromDate = useMemo(() => startOfDay(new Date(periodStart)), [periodStart]);
  const toDateValue = useMemo(() => endOfDay(new Date(periodEnd)), [periodEnd]);

  useEffect(() => {
    setSalesFilters((prev) => ({
      ...prev,
      startDateTime: toInputDateTime(startOfDay(new Date(periodStart))),
      endDateTime: toInputDateTime(endOfDay(new Date(periodEnd))),
    }));
  }, [periodEnd, periodStart]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ordersData, eventsData, salesData, productsData] = await Promise.all([
        getAllOrdini(token, tenant, { noCache: true }),
        getReservationEvents(token, tenant),
        getStatsSales(token, tenant).catch(() => []),
        getStatsProducts(token, tenant).catch(() => []),
      ]);
      setOrders(Array.isArray(ordersData) ? ordersData : []);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setSalesStats(Array.isArray(salesData) ? salesData : []);
      setProductStats(Array.isArray(productsData) ? productsData : []);
    } catch (err) {
      setError(localizedErrorMessage(err, rt("loadError")));
    } finally {
      setLoading(false);
    }
  }, [tenant, token, rt]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const eventDays = useMemo(() => flattenEventDays(events, t), [events, t]);

  const periodOrders = useMemo(
    () =>
      orders.filter(
        (order) => inRange(order.createdAt, fromDate, toDateValue) && order.status !== "CANCELLED"
      ),
    [fromDate, orders, toDateValue]
  );

  const paidOrders = useMemo(
    () => periodOrders.filter((order) => order.status === "PAID"),
    [periodOrders]
  );

  const allPaidOrders = useMemo(
    () => orders.filter((order) => order.status === "PAID"),
    [orders]
  );

  const openOrders = useMemo(
    () => orders.filter((order) => ["OPEN", "IN_PROGRESS", "CLOSED"].includes(order.status)),
    [orders]
  );

  const todayEventDays = useMemo(
    () =>
      eventDays
        .filter(
          (day) =>
            sameDay(day.dayDate || day.startDate, today) &&
            !CANCELLED_EVENT_STATUSES.has(day.eventStatus)
        )
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)),
    [eventDays, today]
  );

  const futureEventDays = useMemo(
    () =>
      eventDays
        .filter((day) => {
          const start = toDate(day.startDate || day.dayDate);
          return start && start >= today && ACTIVE_EVENT_STATUSES.has(day.eventStatus);
        })
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)),
    [eventDays, today]
  );

  const periodEventDays = useMemo(
    () =>
      eventDays.filter(
        (day) =>
          inRange(day.dayDate || day.startDate, fromDate, toDateValue) &&
          !CANCELLED_EVENT_STATUSES.has(day.eventStatus)
      ),
    [eventDays, fromDate, toDateValue]
  );

  // FIX 4: pass rt as second arg, t as third
  const servicesToday = useMemo(
    () => todayEventDays.flatMap((day) => serviceRowsForDay(day, rt, t)),
    [todayEventDays, rt, t]
  );

  const servicesInPeriod = useMemo(
    () => periodEventDays.flatMap((day) => serviceRowsForDay(day, rt, t)),
    [periodEventDays, rt, t]
  );

  const periodProduction = useMemo(
    () => periodOrders.reduce((sum, order) => sum + orderTotal(order), 0),
    [periodOrders]
  );

  const paidProduction = useMemo(
    () => paidOrders.reduce((sum, order) => sum + orderTotal(order), 0),
    [paidOrders]
  );

  const counterOrders = useMemo(
    () => periodOrders.filter((order) => order.tableId == null),
    [periodOrders]
  );

  const counterProduction = useMemo(
    () => counterOrders.reduce((sum, order) => sum + orderTotal(order), 0),
    [counterOrders]
  );

  const plannedProduction = useMemo(
    () => periodEventDays.reduce((sum, day) => sum + eventDayTotal(day), 0),
    [periodEventDays]
  );

  const topServices = useMemo(() => {
    const map = new Map();
    servicesInPeriod.forEach((row) => {
      const key = `${row.type}-${row.name}`;
      const current = map.get(key) || {
        name: row.name,
        type: row.type,
        quantity: 0,
        total: 0,
      };
      current.quantity += row.quantity;
      current.total += row.total;
      map.set(key, current);
    });
    return [...map.values()].sort((a, b) => b.total - a.total).slice(0, 8);
  }, [servicesInPeriod]);

  const usageTypeStats = useMemo(() => {
    const map = new Map();
    periodEventDays.forEach((day) => {
      const event = day.event || {};
      usageTypesForDay(day).forEach((usageType) => {
        const current = map.get(usageType.key) || {
          key: usageType.key,
          name: usageType.name || rt("withoutUsageType"),
          days: 0,
          practices: new Set(),
          guests: 0,
          planned: 0,
        };
        current.days += 1;
        if (event.id) current.practices.add(event.id);
        current.guests += Number(day.guests || 0);
        current.planned += eventDayTotal(day);
        map.set(usageType.key, current);
      });
    });
    return [...map.values()]
      .map((row) => ({ ...row, practicesCount: row.practices.size }))
      .sort((a, b) => b.days - a.days || b.planned - a.planned);
  }, [periodEventDays, rt]);

  const maxUsageTypeDays = Math.max(1, ...usageTypeStats.map((row) => row.days));

  const quoteConversionStats = useMemo(() => {
    const quoteEvents = events.filter(
      (event) => isQuotePractice(event) && inRange(event.createdAt, fromDate, toDateValue)
    );
    const converted = quoteEvents.filter((event) => Boolean(event.quoteConvertedAt));
    const total = quoteEvents.length;
    const percentage = total > 0 ? Math.round((converted.length / total) * 100) : 0;

    return {
      total,
      converted: converted.length,
      percentage,
    };
  }, [events, fromDate, toDateValue]);

  const quoteConfirmationChart = useMemo(() => ({
    labels: [rt("chartQuotes"), rt("chartConfirmations")],
    values: [
      Math.max(0, quoteConversionStats.total - quoteConversionStats.converted),
      quoteConversionStats.converted,
    ],
  }), [quoteConversionStats, rt]);

  const productionRows = useMemo(() => {
    const orderRows = periodOrders.flatMap((order) => {
      const tableName =
        order.tableId == null
          ? rt("counter")
          : order?.table?.name || `${rt("table")} ${order.tableId}`;

      const productRows = (Array.isArray(order.orderProducts) ? order.orderProducts : []).map(
        (line) => ({
          id: `order-product-${order.id}-${line.id}`,
          source: rt("order", { id: order.id }),
          date: order.createdAt,
          name: line?.product?.name || rt("product"),
          quantity: Number(line.quantity || 0),
          total: lineTotal(line, line?.product?.price),
          context: tableName,
        })
      );

      const packageRows = (Array.isArray(order.orderPackages) ? order.orderPackages : [])
        .filter((line) => !isTastingMenuOrderPackage(line))
        .map(
          (line) => ({
            id: `order-package-${order.id}-${line.id}`,
            source: rt("order", { id: order.id }),
            date: order.createdAt,
            name: line?.package?.name || rt("package"),
            quantity: Number(line.quantity || 0),
            total: lineTotal(line, line?.package?.price),
            context: tableName,
          })
        );

      return [...productRows, ...packageRows];
    });

    const plannedRows = servicesInPeriod.map((row) => ({
      id: row.id,
      source: rt("event"),
      date: row.startDate,
      name: row.name,
      quantity: row.quantity,
      total: row.total,
      context: row.eventName,
    }));

    return [...orderRows, ...plannedRows]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 80);
  }, [periodOrders, servicesInPeriod, rt]);

  const baseSoldRows = useMemo(() => {
    return allPaidOrders.flatMap((order) => {
      const saleDate = orderSaleDate(order);
      const tableName =
        order.tableId == null
          ? rt("counter")
          : order?.table?.name || `${rt("table")} ${order.tableId}`;
      const roomId = order?.table?.room?.id ? `room:${order.table.room.id}` : "counter";
      const roomName = order?.table?.room?.name || rt("counter");
      const operatorId = order?.operator?.id ? `operator:${order.operator.id}` : "";
      const operatorName = operatorLabel(order?.operator, "-");
      const payments = Array.isArray(order?.payments) ? order.payments : [];
      const paymentMethods = uniqueValues(
        payments.map((payment) => String(payment?.method || "").toUpperCase())
      );
      const saleType = orderSaleType(order);
      const saleTypeLabel = saleType === "HOTEL"
        ? rt("saleTypeHotel", { defaultValue: "Hotel guest" })
        : rt("saleTypeWalkIn", { defaultValue: "Walk-in customer" });

      const productRows = (Array.isArray(order.orderProducts) ? order.orderProducts : []).map((line) => ({
        id: `sold-product-${order.id}-${line.id}`,
        orderId: order.id,
        date: saleDate,
        type: rt("product"),
        articleKind: "PRODUCT",
        name: line?.product?.name || rt("product"),
        articleKey: `product:${line?.productId ?? line?.product?.id ?? line.id}`,
        articleLabel: line?.product?.name || rt("product"),
        categoryIds: categoryOptionsFromProduct(line?.product).map((category) => category.value),
        categoryLabels: categoryOptionsFromProduct(line?.product).map((category) => category.label),
        roomId,
        roomName,
        operatorId,
        operatorName,
        paymentMethods,
        saleType,
        saleTypeLabel,
        quantity: Number(line.quantity || 0),
        total: lineTotal(line, line?.product?.price),
        context: tableName,
      }));

      const packageRows = (Array.isArray(order.orderPackages) ? order.orderPackages : [])
        .filter((line) => !isTastingMenuOrderPackage(line))
        .map((line) => {
          const packageCategories = categoriesFromPackage(line?.package);
          return {
            id: `sold-package-${order.id}-${line.id}`,
            orderId: order.id,
            date: saleDate,
            type: rt("package"),
            articleKind: "PACKAGE",
            name: line?.package?.name || rt("package"),
            articleKey: `package:${line?.packageId ?? line?.package?.id ?? line.id}`,
            articleLabel: line?.package?.name || rt("package"),
            categoryIds: packageCategories.map((category) => category.value),
            categoryLabels: packageCategories.map((category) => category.label),
            roomId,
            roomName,
            operatorId,
            operatorName,
            paymentMethods,
            saleType,
            saleTypeLabel,
            quantity: Number(line.quantity || 0),
            total: lineTotal(line, line?.package?.price),
            context: tableName,
          };
        });

      return [...productRows, ...packageRows];
    });
  }, [allPaidOrders, rt]);

  const salesFilterOptions = useMemo(() => ({
    categories: uniqueByValue(
      baseSoldRows.flatMap((row) =>
        row.categoryIds.map((value, index) => ({
          value,
          label: row.categoryLabels[index] || value,
        }))
      )
    ),
    articles: uniqueByValue(
      baseSoldRows.map((row) => ({ value: row.articleKey, label: row.articleLabel }))
    ),
    rooms: uniqueByValue(
      baseSoldRows.map((row) => ({ value: row.roomId, label: row.roomName }))
    ),
    operators: uniqueByValue(
      baseSoldRows.map((row) => ({ value: row.operatorId, label: row.operatorName }))
    ),
    paymentMethods: uniqueByValue(
      [
        ...KNOWN_PAYMENT_METHODS.map((method) => ({
          value: method,
          label: paymentLabel(method, rt),
        })),
        ...baseSoldRows.flatMap((row) =>
          row.paymentMethods.map((method) => ({ value: method, label: paymentLabel(method, rt) }))
        ),
      ]
    ),
  }), [baseSoldRows, rt]);

  const saleTypeOptions = useMemo(() => [
    { value: "HOTEL", label: rt("saleTypeHotel", { defaultValue: "Hotel guest" }) },
    { value: "PASSANTE", label: rt("saleTypeWalkIn", { defaultValue: "Walk-in customer" }) },
  ], [rt]);

  const timeSlotOptions = useMemo(
    () => TIME_SLOT_FILTERS.map((slot) => ({
      ...slot,
      label: rt(slot.labelKey, { defaultValue: slot.defaultLabel }),
    })),
    [rt]
  );

  const updateSalesFilter = useCallback((key, value) => {
    setSalesFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetSalesFilters = useCallback(() => {
    setSalesFilters({
      categoryIds: [],
      articleKeys: [],
      roomIds: [],
      operatorIds: [],
      timeSlots: [],
      saleTypes: [],
      paymentMethods: [],
      startDateTime: toInputDateTime(startOfDay(new Date(periodStart))),
      endDateTime: toInputDateTime(endOfDay(new Date(periodEnd))),
    });
    setSoldSearch("");
  }, [periodEnd, periodStart]);

  const soldRows = useMemo(() => {
    const startDateTime = toDate(salesFilters.startDateTime);
    const endDateTime = toDate(salesFilters.endDateTime);

    const filteredByDimensions = baseSoldRows.filter((row) => {
      const rowDate = toDate(row.date);
      if (startDateTime && (!rowDate || rowDate < startDateTime)) return false;
      if (endDateTime && (!rowDate || rowDate > endDateTime)) return false;
      if (salesFilters.categoryIds.length && !row.categoryIds.some((id) => salesFilters.categoryIds.includes(id))) return false;
      if (salesFilters.articleKeys.length && !salesFilters.articleKeys.includes(row.articleKey)) return false;
      if (salesFilters.roomIds.length && !salesFilters.roomIds.includes(row.roomId)) return false;
      if (salesFilters.operatorIds.length && !salesFilters.operatorIds.includes(row.operatorId)) return false;
      if (salesFilters.paymentMethods.length && !row.paymentMethods.some((method) => salesFilters.paymentMethods.includes(method))) return false;
      if (salesFilters.saleTypes.length && !salesFilters.saleTypes.includes(row.saleType)) return false;
      if (!matchesTimeSlot(row.date, salesFilters.timeSlots)) return false;
      return true;
    });

    const search = soldSearch.trim().toLowerCase();
    const filtered = search
      ? filteredByDimensions.filter((row) => [
          row.orderId,
          row.type,
          row.name,
          row.context,
          row.roomName,
          row.operatorName,
          row.saleTypeLabel,
          row.paymentMethods.map((method) => paymentLabel(method, rt)).join(" "),
          row.categoryLabels.join(" "),
          formatDate(row.date, true),
        ].join(" ").toLowerCase().includes(search))
      : filteredByDimensions;

    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [baseSoldRows, rt, salesFilters, soldSearch]);

  useEffect(() => {
    setSoldRowsPage(0);
  }, [salesFilters, soldSearch]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(soldRows.length / soldRowsPerPage) - 1);
    setSoldRowsPage((prev) => Math.min(prev, maxPage));
  }, [soldRows.length, soldRowsPerPage]);

  const pagedSoldRows = useMemo(() => {
    const start = soldRowsPage * soldRowsPerPage;
    return soldRows.slice(start, start + soldRowsPerPage);
  }, [soldRows, soldRowsPage, soldRowsPerPage]);

  const soldRowsTotal = useMemo(
    () => soldRows.reduce((sum, row) => sum + row.total, 0),
    [soldRows]
  );

  const periodSoldRows = useMemo(
    () => baseSoldRows.filter((row) => inRange(row.date, fromDate, toDateValue)),
    [baseSoldRows, fromDate, toDateValue]
  );

  const fixedSalesCharts = useMemo(() => {
    const byType = new Map();
    periodSoldRows.filter((row) => row.articleKind !== "PACKAGE").forEach((row) => {
      byType.set(row.type, Number(byType.get(row.type) || 0) + Number(row.total || 0));
    });
    const composition = [...byType.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
    const counterValue = counterOrders.reduce((sum, order) => sum + orderTotal(order), 0);
    const tableValue = Math.max(0, periodProduction - counterValue);

    return {
      collection: {
        labels: [rt("chartCollected"), rt("chartToCollect")],
        values: [paidProduction, Math.max(0, periodProduction - paidProduction)],
      },
      channel: {
        labels: [rt("chartCounter"), rt("chartTable")],
        values: [counterValue, tableValue],
      },
      composition: {
        labels: composition.map(([label]) => label),
        values: composition.map(([, value]) => value),
      },
    };
  }, [counterOrders, paidProduction, periodProduction, periodSoldRows, rt]);

  const dynamicSalesCharts = useMemo(() => {
    const start = toDate(salesFilters.startDateTime);
    const end = toDate(salesFilters.endDateTime);
    const spanDays = start && end ? Math.max(1, (end - start) / 86400000) : 31;
    const granularity = spanDays <= 2 ? "hour" : spanDays > 120 ? "month" : "day";
    const trend = new Map();

    soldRows.forEach((row) => {
      const date = toDate(row.date);
      if (!date) return;
      let bucketDate;
      let label;
      if (granularity === "hour") {
        bucketDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours());
        label = bucketDate.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit" });
      } else if (granularity === "month") {
        bucketDate = new Date(date.getFullYear(), date.getMonth(), 1);
        label = bucketDate.toLocaleDateString("it-IT", { month: "short", year: "2-digit" });
      } else {
        bucketDate = startOfDay(date);
        label = bucketDate.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
      }
      const key = bucketDate.getTime();
      const current = trend.get(key) || { label, total: 0, quantity: 0 };
      current.total += Number(row.total || 0);
      current.quantity += Number(row.quantity || 0);
      trend.set(key, current);
    });

    const points = [...trend.entries()].sort(([a], [b]) => a - b).map(([, value]) => value);
    const topArticles = aggregateSoldRowsByArticle(soldRows);
    return { points, topArticles };
  }, [salesFilters.endDateTime, salesFilters.startDateTime, soldRows]);

  const dynamicTrendData = useMemo(() => ({
    labels: dynamicSalesCharts.points.map((point) => point.label),
    datasets: [
      {
        label: rt("chartRevenue"),
        data: dynamicSalesCharts.points.map((point) => point.total),
        borderColor: "#F59E0B",
        backgroundColor: "rgba(245,158,11,0.16)",
        pointBackgroundColor: "#0F766E",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: dynamicSalesCharts.points.length > 45 ? 0 : 4,
        pointHoverRadius: 7,
        borderWidth: 3,
        tension: 0.38,
        fill: true,
      },
    ],
  }), [dynamicSalesCharts.points, rt]);

  const dynamicTrendOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    animation: { duration: 700, easing: "easeOutQuart" },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 10, font: { weight: 700 } } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(148,163,184,0.15)" },
        ticks: { callback: (value) => `${Number(value).toLocaleString("it-IT")} €` },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        padding: 12,
        cornerRadius: 10,
        callbacks: { label: (context) => `${rt("chartRevenue")}: ${formatMoney(context.raw)}` },
      },
    },
  }), [rt]);

  const handleSoldRowsPageChange = useCallback((_, page) => {
    setSoldRowsPage(page);
  }, []);

  const handleSoldRowsPerPageChange = useCallback((event) => {
    setSoldRowsPerPage(parseInt(event.target.value, 10));
    setSoldRowsPage(0);
  }, []);

  const productUnitById = useMemo(() => {
    const map = new Map();
    orders.forEach((order) => {
      (Array.isArray(order.orderProducts) ? order.orderProducts : []).forEach((line) => {
        const product = line?.product;
        const unit = weightUnitForProduct(product);
        const productId = line?.productId ?? product?.id;
        if (productId != null && unit) {
          map.set(String(productId), unit);
        }
      });
    });
    return map;
  }, [orders]);

  const calendarDays = useMemo(
    () =>
      Array.from({ length: 14 }, (_, index) => {
        const date = addDays(today, index);
        const rows = futureEventDays.filter((day) =>
          sameDay(day.dayDate || day.startDate, date)
        );
        return { date, rows };
      }),
    [futureEventDays, today]
  );

  const maxTopServiceTotal = Math.max(1, ...topServices.map((row) => row.total));

  const clientRows = useMemo(() => {
    const map = new Map();

    periodOrders.forEach((order) => {
      const clientId =
        order.clientId ??
        order.client?.id ??
        order.companyId ??
        order.company?.id ??
        `UNKNOWN-${order.id}`;

      const clientLabel =
        order.company?.companyName ||
        (order.client
          ? [order.client.name, order.client.surname].filter(Boolean).join(" ") ||
            order.client.email
          : rt("anonymousClient"));

      const contextLabel =
        order.tableId == null
          ? rt("counter")
          : order?.table?.name || `${rt("table")} ${order.tableId}`;

      const key = String(clientId);
      const current =
        map.get(key) || {
          key,
          label: clientLabel,
          contextLabel,
          ordersCount: 0,
          rowsCount: 0,
          total: 0,
        };

      current.ordersCount += 1;
      current.rowsCount += orderArticleCount(order);
      current.total += orderTotal(order);

      map.set(key, current);
    });

    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [periodOrders, rt]);

  const setQuickPeriod = (days) => {
    const end = new Date();
    const start = addDays(end, -(days - 1));
    setPeriodStart(toInputDate(start));
    setPeriodEnd(toInputDate(end));
  };

  // FIX 3: shared sx for the white date TextFields
  const dateFieldSx = {
    width: { xs: "calc(50% - 4px)", sm: 150 }, // two side-by-side on xs, fixed on sm+
    "& .MuiInputLabel-root": {
      color: "rgba(255,255,255,0.85)",
      fontWeight: 700,
      // FIX 3: ensure label sits correctly above the input
      transform: "translate(14px, -9px) scale(0.75)",
      transformOrigin: "top left",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#fff",
    },
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.12)",
      borderRadius: 1,
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.35)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.7)" },
      "&.Mui-focused fieldset": { borderColor: "#fff" },
    },
    "& input": {
      colorScheme: "dark",
      color: "#fff",
    },
  };

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box sx={{ pb: 4, minWidth: 0 }}>
        {/* FIX 1 & 2: overflow changed to visible + clipping box wraps only the decorative circle */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 4,
            color: "#fff",
            background: "linear-gradient(135deg, #12202f 0%, #1f3a3d 46%, #b96500 100%)",
            position: "relative",
            overflow: "visible",   // FIX 1 & 2: was "hidden", caused cut-off
            minWidth: 0,
          }}
        >
          {/* Decorative circle — clipped independently so it doesn't overflow */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: 4,
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: "auto -10% -45% auto",
                width: 360,
                height: 360,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.12)",
              }}
            />
          </Box>

          {/* Header row: stacks vertically on xs/md, side-by-side only on lg+ */}
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ lg: "center" }}
            spacing={2}
            sx={{ position: "relative", minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.8, fontWeight: 800, letterSpacing: 1.2 }}
              >
                {rt("adminReports")}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1.05, mt: 0.5 }}>
                {rt("title")}
              </Typography>
              <Typography sx={{ mt: 1, maxWidth: 760, opacity: 0.92 }}>
                {rt("subtitle")}
              </Typography>
            </Box>

            {/* Date controls: always row, wrap onto next line if needed, never overflow */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: "0 0 auto" }}
            >
              <TextField
                type="date"
                label={rt("from")}
                size="small"
                value={periodStart}
                onChange={(event) => setPeriodStart(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={dateFieldSx}
              />
              <TextField
                type="date"
                label={rt("to")}
                size="small"
                value={periodEnd}
                onChange={(event) => setPeriodEnd(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={dateFieldSx}
              />
              <Button
                variant="contained"
                color="warning"
                startIcon={<RefreshOutlinedIcon />}
                onClick={loadData}
                disabled={loading}
              >
                {rt("refresh")}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" onClick={() => setQuickPeriod(1)}>
            {rt("today")}
          </Button>
          <Button size="small" variant="outlined" onClick={() => setQuickPeriod(7)}>
            {rt("last7")}
          </Button>
          <Button size="small" variant="outlined" onClick={() => setQuickPeriod(30)}>
            {rt("last30")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setPeriodStart(toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)));
              setPeriodEnd(
                toInputDate(new Date(today.getFullYear(), today.getMonth() + 1, 0))
              );
            }}
          >
            {rt("currentMonth")}
          </Button>

          <Button
            size="small"
            variant={showEventsSection ? "contained" : "outlined"}
            color="warning"
            startIcon={<EventAvailableOutlinedIcon />}
            onClick={() => setShowEventsSection((v) => !v)}
          >
            {showEventsSection ? rt("hideEventsSection") : rt("showEventsSection")}
          </Button>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={rt("eventsToday")}
                  value={todayEventDays.length}
                  subtitle={rt("serviceRows", { count: servicesToday.length })}
                  icon={<EventAvailableOutlinedIcon />}
                  color="#1976d2"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={rt("deliverToday")}
                  value={servicesToday
                    .reduce((sum, row) => sum + row.quantity, 0)
                    .toLocaleString("it-IT")}
                  subtitle={rt("plannedServices")}
                  icon={<RoomServiceOutlinedIcon />}
                  color="#ed6c02"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={rt("periodProduction")}
                  value={formatMoney(periodProduction)}
                  subtitle={rt("serviceOrders", { count: periodOrders.length })}
                  icon={<PointOfSaleOutlinedIcon />}
                  color="#2e7d32"
                  progress={periodProduction ? (paidProduction / periodProduction) * 100 : 0}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={rt("plannedEvents")}
                  value={formatMoney(plannedProduction)}
                  subtitle={rt("daysInPeriod", { count: periodEventDays.length })}
                  icon={<CalendarMonthOutlinedIcon />}
                  color="#6d4c41"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={rt("openOrders")}
                  value={openOrders.length}
                  subtitle={rt("openOrdersSubtitle")}
                  icon={<ReceiptLongOutlinedIcon />}
                  color="#7b1fa2"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={rt("counterSales")}
                  value={formatMoney(counterProduction)}
                  subtitle={rt("counterSalesSubtitle", { count: counterOrders.length })}
                  icon={<PointOfSaleOutlinedIcon />}
                  color="#00897b"
                />
              </Grid>
              {isConferenceTenant && (
                <Grid item xs={12} sm={6} lg={2}>
                  <StatCard
                    title={rt("quotesConverted", {
                      defaultValue: "Preventivi convertiti",
                    })}
                    value={`${quoteConversionStats.percentage}%`}
                    subtitle={rt("quotesConvertedSubtitle", {
                      converted: quoteConversionStats.converted,
                      total: quoteConversionStats.total,
                      defaultValue: `${quoteConversionStats.converted} su ${quoteConversionStats.total} preventivi`,
                    })}
                    icon={<TimelineOutlinedIcon />}
                    color="#c2410c"
                    progress={quoteConversionStats.percentage}
                  />
                </Grid>
              )}
            </Grid>

            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: "grid", placeItems: "center", color: "#fff", background: "linear-gradient(135deg, #0F766E, #F59E0B)" }}>
                  <DonutLargeRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={950}>{rt("chartsOverviewTitle")}</Typography>
                  <Typography variant="body2" color="text.secondary">{rt("chartsOverviewSubtitle")}</Typography>
                </Box>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <SalesDonutCard
                    title={rt("chartCollectionTitle")}
                    subtitle={rt("chartCollectionSubtitle")}
                    labels={fixedSalesCharts.collection.labels}
                    values={fixedSalesCharts.collection.values}
                    centerValue={formatMoney(periodProduction)}
                    emptyLabel={rt("chartNoData")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SalesDonutCard
                    title={isConferenceTenant ? rt("chartQuoteConfirmationTitle") : rt("chartChannelTitle")}
                    subtitle={isConferenceTenant ? rt("chartQuoteConfirmationSubtitle") : rt("chartChannelSubtitle")}
                    labels={isConferenceTenant ? quoteConfirmationChart.labels : fixedSalesCharts.channel.labels}
                    values={isConferenceTenant ? quoteConfirmationChart.values : fixedSalesCharts.channel.values}
                    centerValue={isConferenceTenant
                      ? rt("chartPractices", { count: quoteConversionStats.total })
                      : formatMoney(periodProduction)}
                    emptyLabel={rt("chartNoData")}
                    valueFormatter={isConferenceTenant
                      ? (value) => Number(value || 0).toLocaleString("it-IT")
                      : formatMoney}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SalesDonutCard
                    title={rt("chartCompositionTitle")}
                    subtitle={rt("chartCompositionSubtitle")}
                    labels={fixedSalesCharts.composition.labels}
                    values={fixedSalesCharts.composition.values}
                    centerValue={formatMoney(periodSoldRows.reduce((sum, row) => sum + row.total, 0))}
                    emptyLabel={rt("chartNoData")}
                  />
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h5" fontWeight={900}>
                      {rt("servicesToday")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {rt("servicesTodaySubtitle")}
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 430 }}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{rt("time")}</TableCell>
                            <TableCell>{rt("service")}</TableCell>
                            <TableCell>{rt("event")}</TableCell>
                            <TableCell align="right">{rt("quantity")}</TableCell>
                            <TableCell align="right">{rt("value")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {servicesToday.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5}>{rt("noServicesToday")}</TableCell>
                            </TableRow>
                          ) : (
                            servicesToday.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell>{formatTime(row.startDate)}</TableCell>
                                <TableCell>
                                  <Typography fontWeight={800}>{row.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {row.type} | {row.tables}
                                  </Typography>
                                </TableCell>
                                <TableCell>{row.eventName}</TableCell>
                                <TableCell align="right">
                                  {row.quantity.toLocaleString("it-IT")}
                                </TableCell>
                                <TableCell align="right">
                                  {formatMoney(row.total)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography variant="h5" fontWeight={900}>
                          {ordersViewMode === "accounts"
                            ? rt("serviceAccountsTitle")
                            : rt("serviceOrdersTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {ordersViewMode === "accounts"
                            ? rt("serviceAccountsSubtitle")
                            : rt("serviceOrdersSubtitle")}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant={ordersViewMode === "accounts" ? "contained" : "outlined"}
                          onClick={() => setOrdersViewMode("accounts")}
                        >
                          {rt("accountsView")}
                        </Button>
                        <Button
                          size="small"
                          variant={ordersViewMode === "orders" ? "contained" : "outlined"}
                          onClick={() => setOrdersViewMode("orders")}
                        >
                          {rt("ordersView")}
                        </Button>
                      </Stack>
                    </Stack>

                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 430 }}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              {ordersViewMode === "accounts"
                                ? rt("accountColumn")
                                : rt("orderColumn")}
                            </TableCell>
                            <TableCell>{rt("date")}</TableCell>
                            <TableCell>
                              {ordersViewMode === "accounts" ? rt("orders") : rt("status")}
                            </TableCell>
                            <TableCell align="right">{rt("rows")}</TableCell>
                            <TableCell align="right">{rt("total")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ordersViewMode === "accounts" ? (
                            clientRows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5}>{rt("noOrdersPeriod")}</TableCell>
                              </TableRow>
                            ) : (
                              clientRows.slice(0, 40).map((row) => (
                                <TableRow key={row.key} hover>
                                  <TableCell>
                                    <Typography fontWeight={900}>{row.label}</Typography>
                                    {row.contextLabel && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {row.contextLabel}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>-</TableCell>
                                  <TableCell>
                                    {row.ordersCount.toLocaleString("it-IT")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.rowsCount.toLocaleString("it-IT")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatMoney(row.total)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )
                          ) : periodOrders.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5}>{rt("noOrdersPeriod")}</TableCell>
                            </TableRow>
                          ) : (
                            periodOrders.slice(0, 40).map((order) => (
                              <TableRow key={order.id} hover>
                                <TableCell>
                                  <Typography fontWeight={900}>#{order.id}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {order.tableId == null
                                      ? rt("counter")
                                      : order?.table?.name ||
                                        `${rt("table")} ${order.tableId}`}
                                  </Typography>
                                </TableCell>
                                <TableCell>{formatDate(order.createdAt, true)}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    color={statusColor(order.status)}
                                    label={rt(`orderStatus.${order.status}`, {
                                      defaultValue: order.status,
                                    })}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {orderArticleCount(order)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatMoney(orderTotal(order))}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Card sx={{ borderRadius: 3, height: "100%" }}>
                  <CardContent>
                    <Typography variant="h5" fontWeight={900}>
                      {rt("topServices")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {rt("topServicesSubtitle")}
                    </Typography>
                    <Stack spacing={1.4}>
                      {topServices.length === 0 ? (
                        <Alert severity="info">{rt("noServicesPeriod")}</Alert>
                      ) : (
                        topServices.map((row) => (
                          <Box key={`${row.type}-${row.name}`}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Typography fontWeight={800} noWrap>
                                {row.name}
                              </Typography>
                              <Typography fontWeight={900}>
                                {formatMoney(row.total)}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {row.type} | {rt("quantity")}{" "}
                              {row.quantity.toLocaleString("it-IT")}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={(row.total / maxTopServiceTotal) * 100}
                              sx={{ mt: 0.7, height: 8, borderRadius: 99 }}
                            />
                          </Box>
                        ))
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {isConferenceTenant && (
                <Grid item xs={12} lg={7}>
                  <Card sx={{ borderRadius: 3, height: "100%" }}>
                    <CardContent>
                      <Typography variant="h5" fontWeight={900}>
                        {rt("usageTypeStatsTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {rt("usageTypeStatsSubtitle")}
                      </Typography>
                      <Stack spacing={1.4}>
                        {usageTypeStats.length === 0 ? (
                          <Alert severity="info">{rt("noPracticesPeriod")}</Alert>
                        ) : (
                          usageTypeStats.map((row) => (
                            <Box key={row.key}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Box minWidth={0}>
                                  <Typography fontWeight={900} noWrap>{row.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {rt("practiceCount", { count: row.practicesCount })} | {rt("daysCount", { count: row.days })} | {rt("guestsCount", { count: row.guests.toLocaleString("it-IT") })}
                                  </Typography>
                                </Box>
                                <Typography fontWeight={900}>{formatMoney(row.planned)}</Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={(row.days / maxUsageTypeDays) * 100}
                                sx={{ mt: 0.7, height: 8, borderRadius: 99 }}
                              />
                            </Box>
                          ))
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", md: "center" }}
                      spacing={1.5}
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography variant="h5" fontWeight={900}>
                          {rt("soldPeriodTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {rt("soldPeriodSubtitle")}
                        </Typography>
                      </Box>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", sm: "center" }}
                      >
                        <Chip
                          icon={<PointOfSaleOutlinedIcon />}
                          color="success"
                          variant="outlined"
                          label={`${soldRows.length.toLocaleString("it-IT")} righe | ${formatMoney(soldRowsTotal)}`}
                        />
                        <TextField
                          size="small"
                          value={soldSearch}
                          onChange={(event) => setSoldSearch(event.target.value)}
                          placeholder={rt("searchSold", { defaultValue: "Cerca venduto" })}
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                          }}
                          sx={{ minWidth: { xs: 0, sm: 260 } }}
                        />
                      </Stack>
                    </Stack>
                    <Grid container spacing={1.25} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <MultiFilter
                          label={rt("salesFilterCategory")}
                          value={salesFilters.categoryIds}
                          options={salesFilterOptions.categories}
                          onChange={(value) => updateSalesFilter("categoryIds", value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MultiFilter
                          label={rt("salesFilterArticle")}
                          value={salesFilters.articleKeys}
                          options={salesFilterOptions.articles}
                          onChange={(value) => updateSalesFilter("articleKeys", value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MultiFilter
                          label={rt("salesFilterRoom")}
                          value={salesFilters.roomIds}
                          options={salesFilterOptions.rooms}
                          onChange={(value) => updateSalesFilter("roomIds", value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MultiFilter
                          label={rt("salesFilterOperator")}
                          value={salesFilters.operatorIds}
                          options={salesFilterOptions.operators}
                          onChange={(value) => updateSalesFilter("operatorIds", value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MultiFilter
                          label={rt("salesFilterTimeSlot")}
                          value={salesFilters.timeSlots}
                          options={timeSlotOptions}
                          onChange={(value) => updateSalesFilter("timeSlots", value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MultiFilter
                          label={rt("salesFilterSaleType")}
                          value={salesFilters.saleTypes}
                          options={saleTypeOptions}
                          onChange={(value) => updateSalesFilter("saleTypes", value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MultiFilter
                          label={rt("salesFilterPaymentType")}
                          value={salesFilters.paymentMethods}
                          options={salesFilterOptions.paymentMethods}
                          onChange={(value) => updateSalesFilter("paymentMethods", value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Button fullWidth variant="outlined" onClick={resetSalesFilters} sx={{ minHeight: 40 }}>
                          {rt("clearFilters")}
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="datetime-local"
                          label={rt("salesExtractionStart")}
                          value={salesFilters.startDateTime}
                          onChange={(event) => updateSalesFilter("startDateTime", event.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="datetime-local"
                          label={rt("salesExtractionEnd")}
                          value={salesFilters.endDateTime}
                          onChange={(event) => updateSalesFilter("endDateTime", event.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} lg={8}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: { xs: 2, md: 2.5 },
                            height: "100%",
                            minHeight: 330,
                            borderRadius: 4,
                            position: "relative",
                            overflow: "hidden",
                            background: "linear-gradient(145deg, rgba(15,118,110,0.08), transparent 48%, rgba(245,158,11,0.07))",
                          }}
                        >
                          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TrendingUpRoundedIcon sx={{ color: "#0F766E" }} />
                                <Typography variant="h6" fontWeight={950}>{rt("dynamicChartTitle")}</Typography>
                              </Stack>
                              <Typography variant="body2" color="text.secondary">{rt("dynamicChartSubtitle")}</Typography>
                            </Box>
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`${soldRows.length.toLocaleString("it-IT")} ${rt("chartRows")} · ${formatMoney(soldRowsTotal)}`}
                            />
                          </Stack>
                          <Box sx={{ height: 245 }}>
                            {dynamicSalesCharts.points.length ? (
                              <Line data={dynamicTrendData} options={dynamicTrendOptions} />
                            ) : (
                              <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                                <Typography color="text.secondary">{rt("chartNoFilteredData")}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <SalesDonutCard
                          title={rt("dynamicTopArticlesTitle")}
                          subtitle={rt("dynamicTopArticlesSubtitle")}
                          labels={dynamicSalesCharts.topArticles.map(([label]) => label)}
                          values={dynamicSalesCharts.topArticles.map(([, value]) => value)}
                          centerValue={formatMoney(soldRowsTotal)}
                          emptyLabel={rt("chartNoData")}
                        />
                      </Grid>
                    </Grid>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 520, overflowX: "auto" }}
                    >
                      <Table stickyHeader size="small" sx={{ minWidth: 1320 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>{rt("orderColumn")}</TableCell>
                            <TableCell>{rt("salesDateTime")}</TableCell>
                            <TableCell>{rt("salesFilterCategory")}</TableCell>
                            <TableCell>{rt("item")}</TableCell>
                            <TableCell>{rt("context")}</TableCell>
                            <TableCell>{rt("salesFilterRoom")}</TableCell>
                            <TableCell>{rt("salesFilterOperator")}</TableCell>
                            <TableCell>{rt("salesFilterSaleType")}</TableCell>
                            <TableCell>{rt("salesFilterPaymentType")}</TableCell>
                            <TableCell align="right">{rt("quantity")}</TableCell>
                            <TableCell align="right">{rt("total")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {soldRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={11}>
                                {rt("noSoldRows", { defaultValue: "Nessun venduto nel periodo." })}
                              </TableCell>
                            </TableRow>
                          ) : (
                            pagedSoldRows.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell>
                                  <Typography fontWeight={900}>#{row.orderId}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {row.type}
                                  </Typography>
                                </TableCell>
                                <TableCell>{formatDate(row.date, true)}</TableCell>
                                <TableCell>{row.categoryLabels.join(", ") || "-"}</TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.context}</TableCell>
                                <TableCell>{row.roomName}</TableCell>
                                <TableCell>{row.operatorName}</TableCell>
                                <TableCell>{row.saleTypeLabel}</TableCell>
                                <TableCell>{row.paymentMethods.map((method) => paymentLabel(method, rt)).join(", ") || "-"}</TableCell>
                                <TableCell align="right">
                                  {row.quantity.toLocaleString("it-IT")}
                                </TableCell>
                                <TableCell align="right">
                                  {formatMoney(row.total)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                      <TablePagination
                        component="div"
                        count={soldRows.length}
                        page={soldRowsPage}
                        onPageChange={handleSoldRowsPageChange}
                        rowsPerPage={soldRowsPerPage}
                        onRowsPerPageChange={handleSoldRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage={t("common.pagination.rowsPerPage")}
                        labelDisplayedRows={({ from, to, count }) =>
                          t("common.pagination.displayedRows", { from, to, count })
                        }
                      />
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={7}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography variant="h5" fontWeight={900}>
                          {rt("productionTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {rt("productionSubtitle")}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                        justifyContent="flex-end"
                      >
                        <Chip
                          icon={<PointOfSaleOutlinedIcon />}
                          label={`${rt("actual")} ${formatMoney(periodProduction)}`}
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          icon={<TimelineOutlinedIcon />}
                          label={`${rt("planned")} ${formatMoney(plannedProduction)}`}
                          color="warning"
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 430 }}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{rt("source")}</TableCell>
                            <TableCell>{rt("item")}</TableCell>
                            <TableCell>{rt("context")}</TableCell>
                            <TableCell align="right">{rt("quantity")}</TableCell>
                            <TableCell align="right">{rt("total")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productionRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5}>{rt("noCharges")}</TableCell>
                            </TableRow>
                          ) : (
                            productionRows.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell>
                                  <Typography fontWeight={900}>{row.source}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(row.date, true)}
                                  </Typography>
                                </TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.context}</TableCell>
                                <TableCell align="right">
                                  {row.quantity.toLocaleString("it-IT")}
                                </TableCell>
                                <TableCell align="right">
                                  {formatMoney(row.total)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {showEventsSection && (
                <>
                  <Grid item xs={12} xl={7}>
                    <Card sx={{ borderRadius: 3, height: "100%" }}>
                      <CardContent>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Box>
                            <Typography variant="h5" fontWeight={900}>
                              {rt("futureCalendar")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {rt("futureCalendarSubtitle")}
                            </Typography>
                          </Box>
                          <Chip
                            label={rt("futureDays", { count: futureEventDays.length })}
                            color="primary"
                            variant="outlined"
                          />
                        </Stack>
                        <Grid container spacing={1.2}>
                          {calendarDays.map((day) => (
                            <Grid
                              key={day.date.toISOString()}
                              item
                              xs={6}
                              sm={4}
                              md={3}
                              lg={2}
                            >
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.25,
                                  minHeight: 136,
                                  borderRadius: 2,
                                  background: day.rows.length
                                    ? "rgba(212,114,0,0.08)"
                                    : "transparent",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={800}
                                >
                                  {day.date.toLocaleDateString("it-IT", {
                                    weekday: "short",
                                  })}
                                </Typography>
                                <Typography variant="h6" fontWeight={900}>
                                  {day.date.toLocaleDateString("it-IT", {
                                    day: "2-digit",
                                    month: "2-digit",
                                  })}
                                </Typography>
                                <Divider sx={{ my: 0.8 }} />
                                {day.rows.length === 0 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {rt("noEvent")}
                                  </Typography>
                                ) : (
                                  day.rows.slice(0, 3).map((row) => (
                                    <Box
                                      key={`${row.event?.id}-${row.id}`}
                                      sx={{ mb: 0.8 }}
                                    >
                                      <Typography
                                        variant="caption"
                                        fontWeight={900}
                                        noWrap
                                      >
                                        {row.eventName}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color="text.secondary"
                                        noWrap
                                      >
                                        {formatTime(row.startDate)} -{" "}
                                        {tableLabel(row, t)}
                                      </Typography>
                                    </Box>
                                  ))
                                )}
                                {day.rows.length > 3 ? (
                                  <Chip size="small" label={`+${day.rows.length - 3}`} />
                                ) : null}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} xl={5}>
                    <Card sx={{ borderRadius: 3, height: "100%" }}>
                      <CardContent>
                        <Typography variant="h5" fontWeight={900}>
                          {rt("eventsOfDay")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {rt("eventsOfDaySubtitle")}
                        </Typography>
                        <Stack spacing={1.2}>
                          {todayEventDays.length === 0 ? (
                            <Alert severity="info">{rt("noEventsToday")}</Alert>
                          ) : (
                            todayEventDays.map((day) => (
                              <Paper
                                key={`${day.event?.id}-${day.id}`}
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 2 }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Box minWidth={0}>
                                    <Typography fontWeight={900} noWrap>
                                      {day.eventName}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {formatTime(day.startDate)} -{" "}
                                      {formatTime(day.endDate)} | {day.guests}{" "}
                                      {rt("guests")}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      noWrap
                                    >
                                      {day.recipient} | {tableLabel(day, t)}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    color={statusColor(day.eventStatus)}
                                    label={rt(`eventStatus.${day.eventStatus}`, {
                                      defaultValue: day.eventStatus,
                                    })}
                                  />
                                </Stack>
                              </Paper>
                            ))
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h5" fontWeight={900}>
                      {rt("historical")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {rt("historicalSubtitle")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography fontWeight={900} sx={{ mb: 1 }}>
                            {rt("latestDailySales")}
                          </Typography>
                          {salesStats.slice(0, 5).map((row) => (
                            <Stack
                              key={`${row.date}-${row.total_sales}`}
                              direction="row"
                              justifyContent="space-between"
                              sx={{ py: 0.7 }}
                            >
                              <Typography>{formatDate(row.date)}</Typography>
                              <Typography fontWeight={800}>
                                {formatMoney(row.total_sales)} | {row.total_orders}{" "}
                                {rt("orders")}
                              </Typography>
                            </Stack>
                          ))}
                          {salesStats.length === 0 ? (
                            <Typography color="text.secondary">
                              {rt("noHistoricalData")}
                            </Typography>
                          ) : null}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography fontWeight={900} sx={{ mb: 1 }}>
                            {rt("topProducts")}
                          </Typography>
                          {productStats.slice(0, 5).map((row) => {
                            const productName = row.product_name || row.name || rt("product");
                            const productId = row.id ?? row.productId ?? row.product_id;
                            const unit =
                              normalizeWeightUnit(
                                row.unit ||
                                  row.unitOfMeasure ||
                                  row.measureUnit ||
                                  row.typeOfWeight ||
                                  row.typeOfWeightLabel
                              ) ||
                              productUnitById.get(String(productId)) ||
                              "";
                            return (
                              <Stack
                                key={`${productName}-${row.total_quantity}`}
                                direction="row"
                                justifyContent="space-between"
                                sx={{ py: 0.7 }}
                              >
                                <Typography noWrap>{productName}</Typography>
                                <Typography fontWeight={800}>
                                  {formatQuantity(row.total_quantity, unit)} |{" "}
                                  {formatMoney(row.total_revenue)}
                                </Typography>
                              </Stack>
                            );
                          })}
                          {productStats.length === 0 ? (
                            <Typography color="text.secondary">
                              {rt("noProductData")}
                            </Typography>
                          ) : null}
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </SidebarLayout>
  );
};

export default Statistiche;
